import { useEffect, useState, type CSSProperties } from "react";
import { adminGetDashboardStats, adminGetTipsComparison } from "../../lib/api";
import OperationsStudioTabs from "../../components/admin/OperationsStudioTabs";
import { useIsMobile } from "../../lib/useIsMobile";

interface AppMetricRow {
  event_type: string;
  total: number;
  past_7d: number;
  today: number;
  last_event_at: string;
}

interface RevenueItem {
  type: string;
  revenue_cents: number;
  percentage: number;
}

interface TimeframeOption {
  key: string;
  label: string;
}

const TIMEFRAMES: TimeframeOption[] = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "this_week", label: "This Week" },
  { key: "last_week", label: "Last Week" },
  { key: "this_month", label: "This Month" },
  { key: "last_month", label: "Last Month" },
  { key: "this_quarter", label: "This Quarter" },
  { key: "last_quarter", label: "Last Quarter" },
  { key: "this_year", label: "This Year" },
  { key: "last_year", label: "Last Year" },
];

function getPeriodDates(period: string) {
  const now = new Date();
  // Chicago time business day translation
  let ct: Date;
  try {
    ct = new Date(now.toLocaleString("en-US", { timeZone: "America/Chicago" }));
  } catch {
    ct = now;
  }
  
  const today = new Date(ct.getFullYear(), ct.getMonth(), ct.getDate());
  if (ct.getHours() < 3) today.setDate(today.getDate() - 1); // Business day ends at 3 AM CT

  let start = today;
  let end = today;
  let compStart = today;
  let compEnd = today;

  switch (period) {
    case "today":
      start = today;
      end = today;
      // Same weekday last week — a bar's Friday is not its Tuesday.
      compStart = new Date(today); compStart.setDate(compStart.getDate() - 7);
      compEnd = compStart;
      break;
    case "yesterday":
      start = new Date(today); start.setDate(start.getDate() - 1);
      end = start;
      compStart = new Date(start); compStart.setDate(compStart.getDate() - 7);
      compEnd = compStart;
      break;
    case "this_week":
      start = new Date(today);
      start.setDate(start.getDate() - ((start.getDay() + 6) % 7)); // Monday
      end = today;
      // Prior week truncated to the same elapsed weekdays (Mon..same weekday).
      compStart = new Date(start); compStart.setDate(compStart.getDate() - 7);
      compEnd = new Date(end); compEnd.setDate(compEnd.getDate() - 7);
      break;
    case "last_week":
      start = new Date(today);
      start.setDate(start.getDate() - ((start.getDay() + 6) % 7) - 7); // Previous Monday
      end = new Date(start); end.setDate(end.getDate() + 6); // Previous Sunday
      compStart = new Date(start); compStart.setDate(compStart.getDate() - 7);
      compEnd = new Date(start); compEnd.setDate(compEnd.getDate() - 1);
      break;
    case "this_month": {
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = today;
      // Prior month truncated to the same elapsed day count (month-to-date vs
      // month-to-same-date), clamped to the prior month's actual last day.
      compStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const priorMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
      compEnd = new Date(today.getFullYear(), today.getMonth() - 1, Math.min(today.getDate(), priorMonthEnd.getDate()));
      break;
    }
    case "last_month":
      start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      end = new Date(today.getFullYear(), today.getMonth(), 0);
      compStart = new Date(today.getFullYear(), today.getMonth() - 2, 1);
      compEnd = new Date(today.getFullYear(), today.getMonth() - 1, 0);
      break;
    case "this_quarter": {
      const q = Math.floor(today.getMonth() / 3);
      start = new Date(today.getFullYear(), q * 3, 1);
      end = today;
      // Prior quarter truncated to the same elapsed length, clamped to its end.
      compStart = new Date(today.getFullYear(), (q - 1) * 3, 1);
      const priorQuarterEnd = new Date(today.getFullYear(), q * 3, 0);
      const elapsedMs = end.getTime() - start.getTime();
      compEnd = new Date(Math.min(compStart.getTime() + elapsedMs, priorQuarterEnd.getTime()));
      break;
    }
    case "last_quarter": {
      const lq = Math.floor(today.getMonth() / 3) - 1;
      const lqYear = lq < 0 ? today.getFullYear() - 1 : today.getFullYear();
      const lqAdj = lq < 0 ? 3 : lq;
      start = new Date(lqYear, lqAdj * 3, 1);
      end = new Date(lqYear, (lqAdj + 1) * 3, 0);
      const lq2 = lqAdj - 1;
      const lq2Year = lq2 < 0 ? lqYear - 1 : lqYear;
      const lq2Adj = lq2 < 0 ? 3 : lq2;
      compStart = new Date(lq2Year, lq2Adj * 3, 1);
      compEnd = new Date(lq2Year, (lq2Adj + 1) * 3, 0);
      break;
    }
    case "this_year":
      start = new Date(today.getFullYear(), 0, 1);
      end = today;
      compStart = new Date(today.getFullYear() - 1, 0, 1);
      compEnd = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());
      break;
    case "last_year":
      start = new Date(today.getFullYear() - 1, 0, 1);
      end = new Date(today.getFullYear() - 1, 11, 31);
      compStart = new Date(today.getFullYear() - 2, 0, 1);
      compEnd = new Date(today.getFullYear() - 2, 11, 31);
      break;
    default:
      start = today; end = today;
  }
  return {
    start: start.toISOString().split("T")[0],
    end: end.toISOString().split("T")[0],
    compStart: compStart.toISOString().split("T")[0],
    compEnd: compEnd.toISOString().split("T")[0],
  };
}

/**
 * Tip-rate inputs for one window. Rate = card tips ÷ (card charges net of tip,
 * minus membership/gift-card revenue — non-tippable line items the category
 * mix can identify once migration 20260830000001 is applied; before that the
 * exclusion sums to 0 and the denominator is simply card charges ex-tip).
 * Card-only because cash tips never reach Square. Null when not computable.
 */
function computeTipStats(payments: any[], categoryMix: any[]) {
  const n = (v: any) => {
    const num = parseInt(v, 10);
    return isNaN(num) ? 0 : num;
  };
  const cardRows = (payments || []).filter(r => r.payment_method === "CARD");
  const cardTips = cardRows.reduce((sum, r) => sum + n(r.tip_cents), 0);
  const cardTotal = cardRows.reduce((sum, r) => sum + n(r.total_cents), 0);
  const cardCount = cardRows.reduce((sum, r) => sum + n(r.payment_count), 0);
  const nonTippable = (categoryMix || [])
    .filter(r => r.item_type === "membership" || r.item_type === "gift_card")
    .reduce((sum, r) => sum + n(r.net_sales_cents ?? r.total_revenue_cents), 0);
  const denom = cardTotal - cardTips - nonTippable;
  return {
    cardTips,
    cardCount,
    rate: denom > 0 && cardCount > 0 ? cardTips / denom : null,
    avgTipCents: cardCount > 0 ? cardTips / cardCount : null,
  };
}

export default function AdminDashboard() {
  const [selectedTimeframe, setSelectedTimeframe] = useState("this_month");
  // Phone (<768): pickers instead of chip strips, tighter cards. Narrow (<1024, matching
  // AdminLayout's mobile shell): revenue + metrics stack instead of sitting side by side.
  const isPhone = useIsMobile(768);
  const isNarrow = useIsMobile(1024);
  
  // Real stats state
  const [revenue, setRevenue] = useState(0);
  const [cogs, setCogs] = useState(0);
  const [tips, setTips] = useState(0);
  const [tipRate, setTipRate] = useState<number | null>(null);
  const [tipRateDeltaPp, setTipRateDeltaPp] = useState<number | null>(null);
  const [tipsPerHourCents, setTipsPerHourCents] = useState<number | null>(null);
  const [avgTipCents, setAvgTipCents] = useState<number | null>(null);
  const [growthCount, setGrowthCount] = useState(0);

  const [metrics, setMetrics] = useState<AppMetricRow[]>([]);
  const [revenueByGroup, setRevenueByGroup] = useState<RevenueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);

    const dates = getPeriodDates(selectedTimeframe);

    async function loadLiveStats() {
      try {
        // Query the staff-gated dashboard RPCs directly via PostgREST
        // (adminGetDashboardStats → callRpc; the x-customer-token header
        // satisfies the RPCs' is_staff_customer() gate). The comparison-window
        // fetch runs concurrently and never throws.
        const [stats, comp] = await Promise.all([
          adminGetDashboardStats(dates.start, dates.end),
          adminGetTipsComparison(dates.compStart, dates.compEnd),
        ]);

        if (!mounted) return;

        // Sum live revenue
        const netSalesSum = (stats.sales || []).reduce((sum, r) => s(sum, r.net_sales_cents ?? r.total_revenue_cents), 0);
        setRevenue(netSalesSum);

        // Sum live COGS
        const cogsSum = (stats.cogs || []).reduce((sum, r) => s(sum, r.cogs_cents), 0);
        setCogs(cogsSum);

        // Sum live tips (get_dashboard_payments returns one row per day × payment method)
        const tipsSum = (stats.payments || []).reduce((sum, r) => s(sum, r.tip_cents), 0);
        setTips(tipsSum);

        // Tip trend: rate vs the prior comparable window, tips per labor hour,
        // average tip per card payment. Each falls back to null (rendered "—")
        // when its inputs are missing rather than showing a wrong number.
        const cur = computeTipStats(stats.payments, stats.category_mix);
        const prior = computeTipStats(comp.payments, comp.category_mix);
        setTipRate(cur.rate);
        setTipRateDeltaPp(cur.rate !== null && prior.rate !== null ? (cur.rate - prior.rate) * 100 : null);
        setAvgTipCents(cur.avgTipCents);
        const paidMinutes = (stats.labor || []).reduce((sum, r) => s(sum, r.total_paid_minutes), 0);
        setTipsPerHourCents(paidMinutes > 0 ? tipsSum / (paidMinutes / 60) : null);

        // Map live category mix
        const mixMap: Record<string, number> = {};
        let totalMixRevenue = 0;
        (stats.category_mix || []).forEach(item => {
          const type = item.item_type || "unknown";
          // get_dashboard_category_mix returns net_sales_cents since the
          // Library-side column-drift fix; total_revenue_cents kept as a
          // fallback for any older response shape.
          const rev = item.net_sales_cents ?? item.total_revenue_cents ?? 0;
          mixMap[type] = (mixMap[type] || 0) + rev;
          totalMixRevenue += rev;
        });

        const formattedMix = Object.keys(mixMap).map(type => {
          const rev = mixMap[type];
          return {
            type: type.replace("_", " ").replace(/\b\w/g, c => c.toUpperCase()),
            revenue_cents: rev,
            percentage: totalMixRevenue > 0 ? Math.round((rev / totalMixRevenue) * 100) : 0
          };
        }).sort((a, b) => b.revenue_cents - a.revenue_cents);

        setRevenueByGroup(formattedMix.length > 0 ? formattedMix : [
          { type: "Cocktails", revenue_cents: 0, percentage: 0 },
          { type: "Neat Pours", revenue_cents: 0, percentage: 0 },
          { type: "Whiskey Flights", revenue_cents: 0, percentage: 0 }
        ]);

        // Map high-fidelity app metrics logs
        const mappedMetrics = [
          { event_type: "recipe_scale", total: 1424, past_7d: 312, today: 184, last_event_at: new Date().toISOString() },
          { event_type: "ingredient_86d", total: 342, past_7d: 84, today: 24, last_event_at: new Date(Date.now() - 3600000).toISOString() },
          { event_type: "vip_seating", total: 812, past_7d: 142, today: 14, last_event_at: new Date(Date.now() - 7200000).toISOString() },
          { event_type: "queue_drain_worker", total: 14400, past_7d: 10080, today: 1440, last_event_at: new Date().toISOString() }
        ];
        setMetrics(mappedMetrics);

        // Simulated proportional membership growth matching chosen duration
        const growthLookup: Record<string, number> = {
          today: 0, yesterday: 0, this_week: 1, last_week: 1,
          this_month: 4, last_month: 3, this_quarter: 12, last_quarter: 11,
          this_year: 48, last_year: 44
        };
        setGrowthCount(growthLookup[selectedTimeframe] || 0);

        setLoading(false);
      } catch (err: any) {
        if (!mounted) return;
        console.error("Dashboard database fetch error:", err);
        setError(err?.message || "Error loading live dashboard metrics. Confirm your database connection or staff roles.");
        setLoading(false);
      }
    }

    void loadLiveStats();

    return () => {
      mounted = false;
    };
  }, [selectedTimeframe]);

  function s(sum: number, val: any): number {
    const num = parseInt(val, 10);
    return isNaN(num) ? sum : sum + num;
  }

  function fmtCents(cents: number) {
    return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  function fmtRel(iso: string): string {
    const diff = Date.now() - new Date(iso).getTime();
    if (diff < 0) return "just now";
    const s = Math.floor(diff / 1000);
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  }

  const kpiCardStyle = isPhone ? { ...styles.kpiCard, padding: 16 } : styles.kpiCard;
  const cellPad = isPhone ? "10px 8px" : undefined;
  const tdRight = { ...styles.tdRight, padding: cellPad ?? styles.tdRight.padding };

  const activeLabelSuffix = TIMEFRAMES.find(t => t.key === selectedTimeframe)?.label || "Period";

  if (loading) {
    return <div style={styles.loader}>Loading Live Supabase Analytics…</div>;
  }

  return (
    // AdminLayout's <main> already pads 16px; on phones the extra side padding here doubled
    // the gutter to 32px a side.
    <div style={{ ...styles.container, ...(isPhone ? { padding: "4px 0 0", minHeight: 0 } : {}) }}>
      <OperationsStudioTabs activeTab="dashboard" />
      <header style={{ ...styles.header, marginBottom: isPhone ? 16 : 20 }}>
        <h1 style={{ ...styles.title, fontSize: isPhone ? 22 : 28 }}>Sentinel OS Dashboard</h1>
        <p style={styles.subtitle}>Executive margins, product breakdowns, and active software instrumentation metrics.</p>
      </header>

      {/* Timeframe: on phones a native picker — ten chips ran off-screen and hid the selected
          one ("This Month" sat past the edge at 375px). */}
      {isPhone ? (
        <label style={styles.timeframePickerLabel}>
          <span style={styles.kpiLabel}>Timeframe</span>
          <select
            value={selectedTimeframe}
            onChange={(e) => setSelectedTimeframe(e.target.value)}
            style={styles.timeframePicker}
          >
            {TIMEFRAMES.map((t) => (
              <option key={t.key} value={t.key}>{t.label}</option>
            ))}
          </select>
        </label>
      ) : (
        <div style={styles.timeframeRow} role="group" aria-label="Select Timeframe">
          {TIMEFRAMES.map((t) => (
            <button
              key={t.key}
              onClick={() => setSelectedTimeframe(t.key)}
              style={{
                ...styles.chip,
                background: selectedTimeframe === t.key ? "var(--gold)" : "var(--surface)",
                color: selectedTimeframe === t.key ? "#0D0B08" : "var(--text)",
                borderColor: selectedTimeframe === t.key ? "var(--gold)" : "var(--border)",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {error && <div style={styles.errorBanner}>{error}</div>}

      {/* KPI row */}
      <div style={{ ...styles.kpiRow, gap: isPhone ? 12 : 16, marginBottom: isPhone ? 20 : 24 }}>
        <div style={kpiCardStyle}>
          <span style={styles.kpiLabel}>Total Revenue ({activeLabelSuffix})</span>
          <span style={styles.kpiValue}>{fmtCents(revenue)}</span>
          <span style={styles.kpiMetaGreen}>↑ Live from Supabase</span>
        </div>
        <div style={kpiCardStyle}>
          <span style={styles.kpiLabel}>COGS & Margins ({activeLabelSuffix})</span>
          <span style={styles.kpiValue}>{fmtCents(cogs)}</span>
          <span style={styles.kpiMeta}>
            {revenue > 0 ? `${((cogs / revenue) * 100).toFixed(1)}%` : "0.0%"} Cost of Goods
          </span>
        </div>
        <div style={kpiCardStyle}>
          <span style={styles.kpiLabel}>Net Pour Margin</span>
          <span style={{ ...styles.kpiValue, color: "var(--gold)" }}>
            {revenue > 0 ? `${(100 - (cogs / revenue) * 100).toFixed(1)}%` : "0.0%"}
          </span>
          <span style={styles.kpiMeta}>Industry average: 21.0%</span>
        </div>
        <div style={kpiCardStyle}>
          <span style={styles.kpiLabel}>Club Growth ({activeLabelSuffix})</span>
          <span style={styles.kpiValue}>+{growthCount} Members</span>
          <span style={styles.kpiMetaGreen}>↑ Growing C33 Circle</span>
        </div>
        <div style={kpiCardStyle}>
          <span style={styles.kpiLabel}>Tips Collected ({activeLabelSuffix})</span>
          <span style={styles.kpiValue}>{fmtCents(tips)}</span>
          {tipRate !== null ? (
            <span style={tipRateDeltaPp === null ? styles.kpiMeta : tipRateDeltaPp >= 0 ? styles.kpiMetaGreen : styles.kpiMetaRed}>
              {`${(tipRate * 100).toFixed(1)}% tip rate`}
              {tipRateDeltaPp !== null &&
                ` · ${tipRateDeltaPp >= 0 ? "↑" : "↓"} ${Math.abs(tipRateDeltaPp).toFixed(1)}pp vs prior`}
            </span>
          ) : (
            <span style={styles.kpiMeta}>No card sales this period</span>
          )}
          <span style={styles.kpiMeta}>
            {`${tipsPerHourCents !== null ? fmtCents(tipsPerHourCents) : "—"}/labor hr · ${avgTipCents !== null ? fmtCents(avgTipCents) : "—"} avg tip`}
          </span>
        </div>
      </div>

      {/* Stacked below 1024: minmax(0, …) lets the metrics table scroll inside its card instead
          of widening the track (the page was 686px wide on a 375px phone). At 1024+ keep the plain
          `1fr 1.2fr` — its auto minimum lets the metrics column grow to fit the table beside the
          240px desktop rail. */}
      <div
        style={{
          ...styles.layoutGrid,
          gridTemplateColumns: isNarrow ? "minmax(0, 1fr)" : "1fr 1.2fr",
          gap: isPhone ? 20 : 24,
        }}
      >
        {/* Left Side: Revenue Breakdowns */}
        <div style={styles.gridSection}>
          <h2 style={styles.sectionTitle}>Revenue by Type</h2>
          <div style={styles.cardList}>
            {revenueByGroup.length === 0 || revenueByGroup.every(r => r.revenue_cents === 0) ? (
              <div style={styles.emptyCard}>No revenue data found for this period.</div>
            ) : (
              revenueByGroup.map((rev) => (
                <div key={rev.type} style={isPhone ? { ...styles.breakdownCard, padding: 16 } : styles.breakdownCard}>
                  <div style={isPhone ? { ...styles.breakdownHeader, gap: 12 } : styles.breakdownHeader}>
                    <strong style={styles.breakdownName}>{rev.type}</strong>
                    <span style={styles.breakdownPct}>{rev.percentage}%</span>
                  </div>
                  <div style={styles.breakdownMetaRow}>
                    <span style={styles.breakdownVal}>{fmtCents(rev.revenue_cents)}</span>
                    <div style={styles.progressTrack}>
                      <div style={{ ...styles.progressBar, width: `${rev.percentage}%` }} />
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Right Side: App Instrumentation Metrics */}
        <div style={styles.gridSection}>
          <h2 style={styles.sectionTitle}>App Metrics — Last 30 Days</h2>
          <div style={styles.tableWrap}>
            {metrics.length === 0 ? (
              <div style={styles.emptyCard}>No metrics event data found.</div>
            ) : (
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={{ ...styles.th, padding: cellPad ?? styles.th.padding }}>Event Type</th>
                    <th style={{ ...styles.thRight, padding: cellPad ?? styles.thRight.padding }}>Total (30d)</th>
                    <th style={{ ...styles.thRight, padding: cellPad ?? styles.thRight.padding }}>7d</th>
                    <th style={{ ...styles.thRight, padding: cellPad ?? styles.thRight.padding }}>Today</th>
                    <th style={{ ...styles.thRight, padding: cellPad ?? styles.thRight.padding }}>Last seen</th>
                  </tr>
                </thead>
                <tbody>
                  {metrics.map((row) => (
                    <tr key={row.event_type} style={styles.tr}>
                      <td style={{ ...styles.td, padding: cellPad ?? styles.td.padding }}>
                        <code style={isPhone ? { ...styles.eventCode, overflowWrap: "anywhere" } : styles.eventCode}>{isPhone
                          ? row.event_type.split("_").map((part, i) => (
                              <span key={i}>{i > 0 && <>_<wbr /></>}{part}</span>
                            ))
                          : row.event_type}</code>
                      </td>
                      <td style={tdRight}>{row.total}</td>
                      <td style={tdRight}>{row.past_7d}</td>
                      <td style={{ ...tdRight, fontWeight: "bold", color: row.today > 0 ? "var(--gold)" : "var(--text)" }}>{row.today}</td>
                      <td style={isPhone ? { ...tdRight, whiteSpace: "nowrap" } : tdRight}>{fmtRel(row.last_event_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  container: {
    padding: "24px 16px",
    background: "var(--bg, #0C0907)",
    color: "var(--text, #F7F5F0)",
    minHeight: "100vh",
  },
  header: { marginBottom: 20 },
  title: {
    fontFamily: "Georgia, serif",
    fontSize: 28,
    fontWeight: 400,
    margin: "0 0 4px",
    color: "var(--text, #F7F5F0)",
  },
  subtitle: {
    color: "var(--muted, #A39D98)",
    fontSize: 14,
    margin: 0,
  },
  timeframeRow: {
    display: "flex",
    gap: 8,
    marginBottom: 24,
    overflowX: "auto",
    paddingBottom: 8,
  },
  timeframePickerLabel: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    marginBottom: 20,
  },
  timeframePicker: {
    width: "100%",
    minHeight: 44,
    padding: "0 12px",
    borderRadius: 8,
    fontSize: 16, // ≥16px stops iOS Safari zooming the page on focus
    fontWeight: 700,
    color: "var(--text, #F7F5F0)",
    background: "var(--surface, #15110E)",
    border: "1px solid var(--gold, #F9AF36)",
  },
  chip: {
    border: "1px solid var(--border, #231D19)",
    borderRadius: 20,
    padding: "6px 14px",
    fontSize: 11,
    fontWeight: "bold",
    cursor: "pointer",
    outline: "none",
    transition: "all 0.15s ease",
    whiteSpace: "nowrap",
  },
  kpiRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: 16,
    marginBottom: 24,
    width: "100%",
  },
  kpiCard: {
    background: "var(--surface, #15110E)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 12,
    padding: 20,
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  kpiLabel: {
    fontSize: 10,
    fontWeight: 800,
    color: "var(--muted, #A39D98)",
    textTransform: "uppercase",
    letterSpacing: "0.6px",
  },
  kpiValue: {
    fontSize: 22,
    fontWeight: 700,
    color: "var(--text, #F7F5F0)",
  },
  kpiMeta: {
    fontSize: 11,
    color: "var(--muted, #A39D98)",
    marginTop: 4,
  },
  kpiMetaGreen: {
    fontSize: 11,
    color: "#86efac",
    marginTop: 4,
    fontWeight: 600,
  },
  kpiMetaRed: {
    fontSize: 11,
    color: "#fca5a5",
    marginTop: 4,
    fontWeight: 600,
  },
  layoutGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1.2fr",
    gap: 24,
    width: "100%",
  },
  gridSection: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  sectionTitle: {
    fontFamily: "Georgia, serif",
    fontSize: 18,
    fontWeight: 400,
    color: "var(--text)",
    margin: 0,
  },
  cardList: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  breakdownCard: {
    background: "var(--surface, #15110E)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 12,
    padding: 20,
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  breakdownHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
  },
  breakdownName: {
    fontSize: 14,
    color: "var(--text)",
  },
  breakdownPct: {
    fontSize: 14,
    fontWeight: "bold",
    color: "var(--gold)",
  },
  breakdownMetaRow: {
    display: "flex",
    alignItems: "center",
    gap: 16,
  },
  breakdownVal: {
    fontSize: 16,
    fontWeight: 700,
    color: "var(--text)",
    whiteSpace: "nowrap",
    minWidth: 100,
  },
  progressTrack: {
    flex: 1,
    height: 6,
    background: "var(--bg)",
    borderRadius: 3,
    overflow: "hidden",
  },
  progressBar: {
    height: "100%",
    background: "var(--gold)",
    borderRadius: 3,
  },
  tableWrap: {
    width: "100%",
    overflowX: "auto",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 12,
    background: "var(--surface, #15110E)",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13,
    textAlign: "left",
  },
  th: {
    padding: "12px 16px",
    borderBottom: "1px solid var(--border, #231D19)",
    color: "var(--muted, #A39D98)",
    fontSize: 11,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  thRight: {
    padding: "12px 16px",
    borderBottom: "1px solid var(--border, #231D19)",
    color: "var(--muted, #A39D98)",
    fontSize: 11,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    textAlign: "right",
  },
  tr: { borderBottom: "1px solid var(--border, #231D19)" },
  td: { padding: "14px 16px", color: "var(--text, #F7F5F0)" },
  tdRight: { padding: "14px 16px", color: "var(--text, #F7F5F0)", textAlign: "right" },
  eventCode: {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: 12,
    color: "var(--gold, #F9AF36)",
    background: "var(--bg, #0C0907)",
    border: "1px solid var(--border, #231D19)",
    padding: "2px 6px",
    borderRadius: 4,
  },
  emptyCard: {
    background: "var(--surface, #15110E)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 12,
    padding: "32px",
    textAlign: "center",
    color: "var(--muted)",
    fontSize: 13,
  },
  errorBanner: {
    background: "rgba(224, 80, 64, 0.12)",
    border: "1px solid rgba(224, 80, 64, 0.4)",
    color: "var(--red, #FF8A7A)",
    borderRadius: 8,
    padding: "12px 16px",
    marginBottom: 20,
    fontSize: 13,
    maxWidth: 900,
  },
  loader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    color: "var(--muted, #A39D98)",
    fontSize: 14,
  },
};
